import type { Hero, SaveData } from '../game/systems/model';
import { loadSave, writeSave, type KeyValueStorage } from '../game/systems/save';
import { rollDaily } from '../game/systems/daily';
import { t } from '../i18n';

export type Topic = 'hero' | 'monsters' | 'inventory' | 'quests' | 'memories' | 'friends' | 'settings' | 'online' | 'profile';

/**
 * State container for the persistent notebook. Updates are explicit and
 * tagged with topics so UI panels only re-render what changed. Every update
 * schedules a debounced save; pending saves are flushed when the page hides,
 * so a refresh never loses progress.
 */
export class GameStore {
  state: SaveData;
  private subs = new Map<Topic, Set<() => void>>();
  private saveTimer = 0;
  private dirty = false;
  lastError: string | null = null;
  /** Called after every successful write to this browser (cloud sync listens). */
  readonly saved = new Set<() => void>();
  readonly loadedFrom: 'main' | 'backup' | 'new';

  constructor(
    private readonly storage: KeyValueStorage,
    private readonly onSaveError: (msg: string) => void = () => {},
  ) {
    const loaded = loadSave(storage);
    this.state = loaded.save;
    this.loadedFrom = loaded.source;
    window.addEventListener('pagehide', this.flush);
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) this.flush();
    });
  }

  get hero(): Hero | null {
    return this.state.heroes.find((h) => h.id === this.state.activeHeroId) ?? null;
  }

  update(topics: Topic[], mutate: (s: SaveData) => void): void {
    // A new day starts before anything is counted, so daily quests measure from its first update.
    rollDaily(this.state);
    mutate(this.state);
    this.dirty = true;
    window.clearTimeout(this.saveTimer);
    this.saveTimer = window.setTimeout(this.flush, 300);
    const fired = new Set<() => void>();
    for (const t of topics) for (const fn of this.subs.get(t) ?? []) if (!fired.has(fn)) (fired.add(fn), fn());
  }

  subscribe(topics: Topic[], fn: () => void): () => void {
    for (const t of topics) {
      let set = this.subs.get(t);
      if (!set) this.subs.set(t, (set = new Set()));
      set.add(fn);
    }
    return () => topics.forEach((t) => this.subs.get(t)?.delete(fn));
  }

  flush = (): void => {
    if (!this.dirty) return;
    window.clearTimeout(this.saveTimer);
    try {
      writeSave(this.storage, this.state);
      this.dirty = false;
      this.lastError = null;
      for (const fn of this.saved) fn();
    } catch (err) {
      const msg = err instanceof DOMException && err.name === 'QuotaExceededError' ? t('Your notebook is full! Delete some drawings to keep saving.') : t('Could not save the notebook.');
      if (this.lastError !== msg) this.onSaveError(msg);
      this.lastError = msg;
    }
  };

  /** Replace everything (import / reset). */
  replace(next: SaveData): void {
    this.state = next;
    this.dirty = true;
    this.flush();
    for (const set of this.subs.values()) for (const fn of set) fn();
  }
}
